import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

import { ORDENABLE_PAGE_SIZE } from '@/constants';
import { useTreeCourses } from '@/data/context/TreeCoursesContext';
import useCourseTreeNavigate from '@/data/hook/course-line/useCourseTreeNavigate';
import useLessonWorkspace from '@/data/hook/lesson/useLessonWorkspace';
import {
  useInfiniteCache,
  useListCache,
} from '@/data/hook/query/v2/useCacheManager';
import {
  lessonBlocksQueryKeys,
  lessonsQueryKeys,
} from '@/data/services/querykeys';
import { mountKey } from '@/utils/courses';
import { ROUTES } from '@/utils/routes';

const useLessonReplacement = () => {
  const { slug, unitId, lessonId, courseLineId, moduleId } =
    useLessonWorkspace();
  const { tree } = useTreeCourses();
  const { navigate } = useCourseTreeNavigate();
  const queryClient = useQueryClient();
  const { search } = useLocation();

  const cacheManager = useInfiniteCache(
    lessonBlocksQueryKeys.listLessons(slug ?? '', unitId)._ctx.infinity
      .queryKey,
  );

  const dndCacheManager = useListCache(
    lessonBlocksQueryKeys.listLessons(slug ?? '', unitId, {
      pageSize: ORDENABLE_PAGE_SIZE,
    }).queryKey,
  );

  const replaceLesson = async (newLessonId: number) => {
    const versioned = await queryClient.ensureQueryData(
      lessonsQueryKeys.get(newLessonId),
    );
    const oldKey = mountKey({ type: 'lesson', id: lessonId });
    const newKey = mountKey({ type: 'lesson', id: versioned.id });
    const unitKey = mountKey({ type: 'unit', id: unitId });

    cacheManager.update(
      { ...versioned, id: lessonId },
      { newId: versioned.id },
    );
    dndCacheManager.update(
      { ...versioned, id: lessonId },
      { newId: versioned.id },
    );
    tree?.current.delete(oldKey);
    tree?.current.insert(
      {
        id: newKey,
        name: versioned.name,
        type: 'lesson',
        realId: versioned.id,
      },
      unitKey,
    );
    navigate(
      {
        pathname: ROUTES.ADMIN.COURSE_LINES.LESSON(
          courseLineId,
          moduleId,
          slug ?? '',
          unitId,
          versioned.id,
        ),
        search,
      },
      { treeKey: newKey },
    );
  };

  return { replaceLesson };
};

export default useLessonReplacement;
